import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ORGANIZATION_DISPLAY_NAME } from '../config/branding';
import { iamPlatformAPI } from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { resolveLoginErrorMessage } from '../utils/iamLoginError';
import {
  MOBILE_LOGIN_PATH,
  WEB_FIRST_LOGIN_PASSWORD_PATH,
} from '../utils/authRoutes';
import { PORTAL_LOGIN_PATH } from '../utils/portalAdminNav';

import {
  normalizeLoginBrandingFromHint,
  resolveLoginBrandingDisplay,
} from '../utils/loginBranding';

const REMEMBER_KEY = 'entrea_remember_login';

const CapIcon = ({ className = 'h-7 w-7' }) => (
  <svg className={`text-white ${className}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
  </svg>
);

const Spinner = () => (
  <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

function OrgLogoMark({ logoUrl, logoBroken, onLogoError, sizeClass = 'h-11 w-11' }) {
  if (logoUrl && !logoBroken) {
    return (
      <img
        src={logoUrl}
        alt=""
        className={`${sizeClass} rounded-2xl object-cover bg-white/10 ring-1 ring-white/25`}
        onError={onLogoError}
      />
    );
  }
  return (
    <div
      className={`flex ${sizeClass} items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur-sm`}
    >
      <CapIcon className="h-6 w-6" />
    </div>
  );
}

/**
 * Connexion publique web / bureau — écran distinct du parcours mobile (`/mobile/login`).
 */
const LoginWeb = () => {
  const { t } = useTranslation('common');
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [loginBranding, setLoginBranding] = useState(null);
  const [logoBroken, setLogoBroken] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const fetchLoginBranding = useCallback(async (rawUsername) => {
    const q = String(rawUsername || '').trim();
    if (q.length < 2) {
      setOrganizationName('');
      setLoginBranding(null);
      setLogoBroken(false);
      return;
    }
    try {
      const res = await iamPlatformAPI.loginHint(q);
      const name = res.data?.organization_name;
      setOrganizationName(typeof name === 'string' && name.trim() ? name.trim() : '');
      const normalized = normalizeLoginBrandingFromHint(res.data, t);
      setLoginBranding(normalized);
      setLogoBroken(false);
    } catch {
      setOrganizationName('');
      setLoginBranding(null);
    }
  }, [t]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.remember && parsed?.username) {
          setUsername(parsed.username);
          setRemember(true);
          setStep(2);
          fetchLoginBranding(parsed.username);
        }
      }
    } catch {
      /* ignore */
    }
  }, [fetchLoginBranding]);

  useEffect(() => {
    const q = username.trim();
    if (q.length < 2) {
      setOrganizationName('');
      setLoginBranding(null);
      setLogoBroken(false);
      return;
    }
    const id = setTimeout(() => {
      fetchLoginBranding(q);
    }, 420);
    return () => clearTimeout(id);
  }, [username, fetchLoginBranding]);

  const persistRemember = (user, success) => {
    if (!success) return;
    try {
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({ remember: true, username: user }));
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
    } catch {
      /* ignore */
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      if (!username.trim()) {
        setError(t('login.errorIdentifier'));
        return;
      }
      setStep(2);
      setPassword('');
      setShowPassword(false);
      return;
    }

    setLoading(true);
    const result = await login(username, password);

    if (result.success) {
      persistRemember(username.trim(), true);
      if (result.mustChangePassword) {
        navigate(WEB_FIRST_LOGIN_PASSWORD_PATH);
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(resolveLoginErrorMessage(result, t));
    }

    setLoading(false);
  };

  const goBackToIdentifier = () => {
    setStep(1);
    setPassword('');
    setError('');
    setShowPassword(false);
  };

  const inputClass =
    'block w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-base text-slate-900 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 focus:border-entrea-royal focus:outline-none focus:ring-2 focus:ring-entrea-royal/20';

  const primaryBtnClass =
    'flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-entrea-ink to-entrea-royal py-3.5 text-base font-semibold text-white shadow-md shadow-entrea-royal/20 transition hover:from-[#0a2d4a] hover:to-entrea-ink focus:outline-none focus:ring-2 focus:ring-entrea-royal focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

  const brand = resolveLoginBrandingDisplay(loginBranding, t);
  const displayOrgName = organizationName || brand.organizationName || ORGANIZATION_DISPLAY_NAME;

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Panneau gauche — marque organisation (dynamique via login-hint) */}
      <aside className="relative hidden w-[46%] overflow-hidden lg:flex lg:flex-col">
        <img
          src={brand.heroImageUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-entrea-ink/95 via-[#0e3558]/90 to-entrea-royal/85" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 80%, rgba(56,189,248,0.35) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.12) 0%, transparent 40%)',
          }}
          aria-hidden
        />
        <div className="relative z-10 flex flex-1 flex-col justify-between p-10 xl:p-14">
          <Link to="/" className="inline-flex items-center gap-3 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60">
            <OrgLogoMark
              logoUrl={brand.logoUrl}
              logoBroken={logoBroken}
              onLogoError={() => setLogoBroken(true)}
            />
            <div>
              <p className="text-lg font-bold tracking-tight text-white">{displayOrgName}</p>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-100/80">Uruganda Platform</p>
            </div>
          </Link>

          <div className="max-w-md">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-200/90">{brand.heroKicker}</p>
            <h1 className="mt-3 text-4xl font-extrabold leading-tight tracking-tight text-white xl:text-[2.65rem]">
              {brand.heroTitle}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-slate-200/95">{brand.heroSubtitle}</p>

            <ul className="mt-10 space-y-5">
              <li className="flex gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20 backdrop-blur-sm">
                  <svg className="h-5 w-5 text-cyan-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-white">{brand.feature1Title}</p>
                  <p className="mt-0.5 text-sm text-slate-300">{brand.feature1Desc}</p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20 backdrop-blur-sm">
                  <svg className="h-5 w-5 text-cyan-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-white">{brand.feature2Title}</p>
                  <p className="mt-0.5 text-sm text-slate-300">{brand.feature2Desc}</p>
                </div>
              </li>
            </ul>
          </div>

          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} {displayOrgName}
          </p>
        </div>
      </aside>

      {/* Panneau droit — formulaire */}
      <main className="flex flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-slate-200/80 bg-white/80 px-4 py-3 backdrop-blur-sm sm:px-8 lg:border-0 lg:bg-transparent lg:px-10 lg:pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg lg:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-entrea-royal"
          >
            {brand.logoUrl && !logoBroken ? (
              <img
                src={brand.logoUrl}
                alt=""
                className="h-9 w-9 rounded-xl object-cover ring-1 ring-slate-200"
                onError={() => setLogoBroken(true)}
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-entrea-ink to-entrea-royal shadow-sm">
                <CapIcon className="h-5 w-5" />
              </div>
            )}
            <span className="font-bold text-slate-900">{displayOrgName}</span>
          </Link>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 sm:inline-flex ring-1 ring-emerald-100">
              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              {t('loginWeb.secureBadge')}
            </span>
            <LanguageSwitcher />
          </div>
        </header>

        <div className="flex flex-1 items-center justify-center px-4 py-8 sm:px-8 lg:px-12">
          <div className="w-full max-w-[420px]">
            {/* Indicateur d'étape */}
            <div className="mb-6 flex items-center justify-between gap-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t('loginWeb.stepOf', { current: step, total: 2 })}
              </p>
              <div className="flex gap-1.5" aria-hidden>
                <span className={`h-1.5 rounded-full transition-all ${step === 1 ? 'w-8 bg-entrea-royal' : 'w-3 bg-slate-200'}`} />
                <span className={`h-1.5 rounded-full transition-all ${step === 2 ? 'w-8 bg-entrea-royal' : 'w-3 bg-slate-200'}`} />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200/90 bg-white p-6 shadow-xl shadow-slate-200/50 sm:p-8">
              <h2 className="text-2xl font-bold tracking-tight text-[#0a2340]">
                {step === 1 ? t('login.welcomeTitle') : t('login.passwordTitle')}
              </h2>
              <p className="mt-1.5 text-sm text-slate-500">
                {step === 1 ? t('login.subtitleStep1') : t('login.subtitleStep2')}
              </p>

              {organizationName && (
                <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full bg-entrea-sky/15 px-3 py-1.5 ring-1 ring-entrea-royal/15">
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-entrea-royal">
                    {t('login.orgBadge')}
                  </span>
                  <span className="truncate text-sm font-semibold text-entrea-ink">{organizationName}</span>
                </div>
              )}

              {error && (
                <div
                  className="mt-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                  role="alert"
                >
                  <svg className="mt-0.5 h-5 w-5 shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                {step === 1 && (
                  <>
                    <div>
                      <label htmlFor="login-user" className="mb-2 block text-sm font-medium text-slate-700">
                        {t('login.identifierLabel')}
                      </label>
                      <input
                        id="login-user"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        autoComplete="username"
                        className={inputClass}
                        placeholder={t('login.identifierPlaceholder')}
                        autoFocus
                      />
                    </div>
                    <label className="flex cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-entrea-royal focus:ring-entrea-royal"
                      />
                      <span className="text-sm text-slate-600">{t('login.rememberMe')}</span>
                    </label>
                    <button type="submit" className={primaryBtnClass}>
                      {t('login.continue')}
                    </button>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-100">
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500">{t('login.accountLabel')}</p>
                        <p className="truncate font-semibold text-slate-900">{username}</p>
                      </div>
                      <button
                        type="button"
                        onClick={goBackToIdentifier}
                        className="shrink-0 rounded-lg px-2 py-1 text-sm font-medium text-entrea-royal transition hover:bg-entrea-sky/20"
                      >
                        {t('login.change')}
                      </button>
                    </div>
                    <div>
                      <label htmlFor="login-pass" className="mb-2 block text-sm font-medium text-slate-700">
                        {t('login.passwordLabel')}
                      </label>
                      <div className="relative">
                        <input
                          id="login-pass"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="current-password"
                          className={`${inputClass} pr-12`}
                          placeholder="••••••••"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                          aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <button type="submit" disabled={loading} className={primaryBtnClass}>
                      {loading ? (
                        <>
                          <Spinner />
                          {t('login.signingIn')}
                        </>
                      ) : (
                        t('login.signIn')
                      )}
                    </button>
                  </>
                )}
              </form>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-slate-100 pt-5 text-sm">
                <Link
                  to="/forgot-password?topic=username"
                  className="font-medium text-entrea-royal hover:text-entrea-ink hover:underline"
                >
                  {t('login.forgotUsername')}
                </Link>
                <span className="text-slate-300" aria-hidden>
                  ·
                </span>
                <Link
                  to="/forgot-password?topic=password"
                  className="font-medium text-entrea-royal hover:text-entrea-ink hover:underline"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>
            </div>

            <div className="mt-6 space-y-3 text-center text-sm">
              <p>
                <Link to="/" className="font-medium text-slate-600 hover:text-entrea-royal">
                  {t('login.backHome')}
                </Link>
              </p>
              <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-slate-500">
                <Link to={MOBILE_LOGIN_PATH} className="font-medium text-entrea-royal hover:text-entrea-ink">
                  {t('login.mobileLoginCta')}
                </Link>
                <span className="text-slate-300" aria-hidden>
                  ·
                </span>
                <Link to={PORTAL_LOGIN_PATH} className="font-medium text-entrea-royal hover:text-entrea-ink">
                  {t('loginWeb.adminPortalLink')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginWeb;
